import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';

import LandingSection from '../../../MyComponents/LandingSections/LandingSection';
import SectionHeading from '../../../MyComponents/LandingSections/SectionHeading';
import { PROFILE_LINK, SPECIAL_QUOTA_LINK } from '../../../common/urlLinks';
import { featureList, getIntlCount, replaceValues } from '../../../common/features';
import { GA_EVENT_TREE } from '../../../gaConstants';
import { REDESIGN_COLORS, REDESIGN_FONTS } from '../../../constants';
import {
    getSubscriptionStateAction,
    SUBSCRIPTION_PLANS,
    SUBSCRIPTION_STATE_ACTION,
    TRIAL_DAYS,
} from '../../../shared/Capabilities.js';
import { ONBOARDING_STEPS } from '../../../shared/OnboardingOptions';

const ONBOARDING = `/onboarding?step=${ONBOARDING_STEPS.START}&subscription=`;

// The design's price toggles, kept as local UI state.
const MONTHLY = 'monthly';
const ANNUAL = 'annual';
const RON = 'ron';
const EUR = 'eur';

const DEFAULT_HEADING = {
    eyebrow: 'redesign.home.pricing.eyebrow',
    title: 'redesign.home.pricing.title',
    lead: 'redesign.home.pricing.lead',
};

// Module scope on purpose: declaring this inside HomePricing would make it a new
// component type on every render, so React would remount the buttons - losing
// focus - each time a toggle is pressed.
const Toggle = ({ on, onClick, classes, children }) => (
    <button
        type="button"
        aria-pressed={on}
        onClick={onClick}
        className={[classes.toggle, on ? classes.toggleOn : ''].filter(Boolean).join(' ')}
    >
        {children}
    </button>
);

const useStyles = makeStyles(() => ({
    toggles: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '13px',
        margin: '0 0 34px',
    },
    group: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '5px',
        border: `1px solid ${REDESIGN_COLORS.line}`,
        borderRadius: '999px',
        background: REDESIGN_COLORS.toggleTrack,
    },
    toggle: {
        minHeight: '42px',
        padding: '0 18px',
        border: 0,
        borderRadius: '999px',
        background: 'none',
        color: REDESIGN_COLORS.body,
        fontFamily: REDESIGN_FONTS.body,
        fontSize: '0.9rem',
        fontWeight: REDESIGN_FONTS.weightHeading,
        cursor: 'pointer',
    },
    toggleOn: {
        color: REDESIGN_COLORS.white,
        background: REDESIGN_COLORS.primary,
    },
    saveBadge: {
        display: 'inline-block',
        marginLeft: '6px',
        padding: '2px 7px',
        borderRadius: '999px',
        color: REDESIGN_COLORS.dark,
        background: REDESIGN_COLORS.highlight,
        fontSize: '0.68rem',
    },
    // The free-trial offer for signed-in users who have not used it yet.
    trial: {
        boxSizing: 'border-box',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '14px 24px',
        margin: '0 0 26px',
        padding: '20px 24px',
        border: `1px solid ${REDESIGN_COLORS.amberBorder}`,
        borderRadius: '20px',
        background: REDESIGN_COLORS.amberSurface,
    },
    trialTitle: {
        margin: '0 0 4px',
        fontSize: '1.1rem',
        lineHeight: 1.25,
        letterSpacing: '-0.02em',
        fontWeight: REDESIGN_FONTS.weightHeading,
        color: REDESIGN_COLORS.dark,
    },
    trialText: {
        margin: 0,
        color: REDESIGN_COLORS.body,
        fontSize: '0.9rem',
    },
    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 255px), 1fr))',
        gap: '18px',
        alignItems: 'stretch',
    },
    card: {
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        padding: '34px 30px 28px',
        border: `1px solid ${REDESIGN_COLORS.sand}`,
        borderRadius: '26px',
        background: REDESIGN_COLORS.white,
    },
    cardFeatured: {
        position: 'relative',
        border: `2px solid ${REDESIGN_COLORS.primary}`,
        boxShadow: '0 22px 55px rgba(6, 63, 69, 0.12)',
        // No top offset: the card stays aligned with its neighbours and the
        // ribbon overhangs into the gap left by the toggles above.
    },
    ribbon: {
        position: 'absolute',
        top: '-16px',
        left: '50%',
        transform: 'translateX(-50%)',
        padding: '7px 18px',
        borderRadius: '999px',
        color: REDESIGN_COLORS.white,
        background: REDESIGN_COLORS.primary,
        fontSize: '0.7rem',
        fontWeight: REDESIGN_FONTS.weightHeading,
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
    },
    name: {
        margin: '0 0 16px',
        fontSize: '1.6rem',
        letterSpacing: '-0.03em',
        fontWeight: REDESIGN_FONTS.weightHeading,
        color: REDESIGN_COLORS.dark,
    },
    blurb: {
        minHeight: '72px',
        margin: '0 0 18px',
        color: REDESIGN_COLORS.body,
        fontSize: '0.92rem',
    },
    priceRow: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'baseline',
        gap: '5px',
        margin: '8px 0 4px',
        fontSize: '2.5rem',
        fontWeight: REDESIGN_FONTS.weightHeading,
        letterSpacing: '-0.055em',
        color: REDESIGN_COLORS.dark,
    },
    priceRowQuote: {
        fontSize: '2.1rem',
        letterSpacing: '-0.05em',
    },
    priceUnit: {
        color: REDESIGN_COLORS.body,
        fontSize: '0.78rem',
        fontWeight: REDESIGN_FONTS.weightStrong,
        letterSpacing: 0,
    },
    priceNote: {
        minHeight: '24px',
        color: REDESIGN_COLORS.body,
        fontSize: '0.8rem',
    },
    subNote: {
        minHeight: '44px',
        margin: '10px 0 0',
        color: REDESIGN_COLORS.body,
        fontSize: '0.84rem',
    },
    badge: {
        alignSelf: 'flex-start',
        margin: '2px 0 6px',
        padding: '5px 9px',
        borderRadius: '8px',
        color: REDESIGN_COLORS.dark,
        background: REDESIGN_COLORS.amberBadge,
        fontSize: '0.72rem',
        fontWeight: REDESIGN_FONTS.weightHeading,
    },
    bullets: {
        flex: 1,
        display: 'grid',
        alignContent: 'start',
        gap: '12px',
        margin: '18px 0 26px',
        padding: 0,
        listStyle: 'none',
        color: REDESIGN_COLORS.body,
        fontSize: '0.88rem',
    },
    bullet: {
        display: 'flex',
        gap: '9px',
    },
    // A feature the plan does not include: still listed, so the plans stay
    // comparable, but dimmed with a dash instead of a tick.
    bulletOff: {
        display: 'flex',
        gap: '9px',
        opacity: 0.55,
    },
    tickOff: {
        color: REDESIGN_COLORS.body,
        fontWeight: REDESIGN_FONTS.weightHeading,
    },
    tick: {
        color: REDESIGN_COLORS.link,
        fontWeight: REDESIGN_FONTS.weightHeading,
    },
    cta: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxSizing: 'border-box',
        width: '100%',
        minHeight: '48px',
        marginTop: 'auto',
        padding: '0 20px',
        border: `1px solid ${REDESIGN_COLORS.line}`,
        borderRadius: '999px',
        color: REDESIGN_COLORS.dark,
        background: REDESIGN_COLORS.white,
        fontFamily: REDESIGN_FONTS.body,
        fontSize: '1rem',
        fontWeight: REDESIGN_FONTS.weightHeading,
        textAlign: 'center',
        cursor: 'pointer',
        '&:hover': {
            borderColor: REDESIGN_COLORS.primary,
            color: REDESIGN_COLORS.link,
        },
    },
    ctaPrimary: {
        color: REDESIGN_COLORS.white,
        background: REDESIGN_COLORS.primary,
        borderColor: REDESIGN_COLORS.primary,
        '&:hover': {
            color: REDESIGN_COLORS.white,
            background: REDESIGN_COLORS.primaryHover,
            borderColor: REDESIGN_COLORS.primaryHover,
        },
    },
    // The plan the signed-in user is already on: shown, not clickable.
    ctaCurrent: {
        cursor: 'default',
        color: REDESIGN_COLORS.link,
        background: REDESIGN_COLORS.mist,
        borderColor: REDESIGN_COLORS.primaryBorderSoft,
        '&:hover': {
            color: REDESIGN_COLORS.link,
            borderColor: REDESIGN_COLORS.primaryBorderSoft,
        },
    },
    ctaNote: {
        marginTop: 'auto',
        color: REDESIGN_COLORS.body,
        fontSize: '0.8rem',
        textAlign: 'center',
    },
    footnote: {
        margin: '25px auto 0',
        color: REDESIGN_COLORS.body,
        textAlign: 'center',
        fontSize: '0.8rem',
    },
}));

const cx = (...names) => names.filter(Boolean).join(' ');

/**
 * The `#preturi` band. Prices come from mainAppState.pricing - the same public
 * Stripe payload /subscriptions uses - rather than from the design source,
 * whose figures are hardcoded placeholders.
 *
 * Signed out, every plan offers to start it. Signed in, each card reflects the
 * account's plan: the current one is marked, higher ones offer an upgrade, a
 * cancelled paid plan can be reactivated, and the free trial is offered once.
 *
 * `heading` overrides the eyebrow/title/lead ids; `null` drops the heading for
 * pages whose hero already introduces the plans.
 */
const HomePricing = ({ pricing, subscriptions, profile, heading = DEFAULT_HEADING }) => {
    const classes = useStyles();
    const intl = useIntl();
    const [billing, setBilling] = React.useState(ANNUAL);
    // Romanian visitors pay in lei; everyone else sees euros first.
    const [currency, setCurrency] = React.useState(intl.locale === 'ro' ? RON : EUR);

    React.useEffect(() => {
        setCurrency(intl.locale === 'ro' ? RON : EUR);
    }, [intl.locale]);

    const isAnnual = billing === ANNUAL;
    const isEur = currency === EUR;
    const t = (id, values) => intl.formatMessage({ id: `redesign.home.pricing.${id}` }, values);
    const tp = (id, values) => intl.formatMessage({ id: `redesign.pricing.${id}` }, values);

    const log = (event) => {
        if (window.logAppActivity) window.logAppActivity(event);
    };

    const loggedIn = Boolean(profile && profile.loggedIn);
    const stateOf = (type) => getSubscriptionStateAction(profile, subscriptions, type);
    const isCurrent = (type) =>
        loggedIn &&
        (stateOf(type) === SUBSCRIPTION_STATE_ACTION.ACTIVE || stateOf(type) === SUBSCRIPTION_STATE_ACTION.CANCEL);
    const canceled = Boolean(loggedIn && profile.subscription && profile.subscription.canceledAt);
    // A paid plan that is still running has to be cancelled before another
    // paid plan can be started; the account page is where that happens.
    const canUpgrade =
        !loggedIn ||
        canceled ||
        ![SUBSCRIPTION_PLANS.BASIC, SUBSCRIPTION_PLANS.PREMIUM].some(
            (type) => stateOf(type) === SUBSCRIPTION_STATE_ACTION.CANCEL
        );
    const canStartTrial =
        loggedIn && !profile.isGuest && Boolean(profile.subscription) && profile.subscription.trialStarted === false;
    // Signed out the design leads with Standard; signed in, with the user's own plan.
    const featuredType = loggedIn
        ? [SUBSCRIPTION_PLANS.FREE, SUBSCRIPTION_PLANS.BASIC, SUBSCRIPTION_PLANS.PREMIUM].find(isCurrent)
        : SUBSCRIPTION_PLANS.BASIC;

    const onboardingLink = (type) => `${ONBOARDING}${type}&billAnnual=${isAnnual}&billEur=${isEur}`;

    // The plan's real capabilities, from the same public payload and the same
    // featureList/replaceValues pair the subscriptions page uses - rather than a
    // hand-written list that can drift from what the plan actually allows.
    const featuresFor = (type) => {
        const data = subscriptions && subscriptions[type];
        if (!data) return null;
        // Shallow copies: replaceValues mutates the entries.
        const list = featureList.map((f) => ({ ...f }));
        replaceValues(data, list);
        return list;
    };

    const renderFeatures = (type) => {
        const list = featuresFor(type);
        if (!list) return null;

        return list.map((f) => {
            const isCount = typeof f.defaultValue !== 'boolean';
            const included = isCount ? f.defaultValue !== 0 : f.defaultValue;
            const formatObj = { [f.name]: getIntlCount(intl, f.defaultValue) };
            // Counts carry a separate label key; booleans are their own label.
            const label = intl.formatMessage(
                { id: (isCount ? 'redesign.subscriptions.feature.' : 'subscriptions.feature.') + f.name + '.name' },
                formatObj
            );
            const value = isCount
                ? intl.formatMessage({ id: 'subscriptions.feature.' + f.name + '.name' }, formatObj)
                : null;

            return (
                <li key={f.name} className={included ? classes.bullet : classes.bulletOff}>
                    <span aria-hidden="true" className={included ? classes.tick : classes.tickOff}>
                        {included ? '✓' : '—'}
                    </span>
                    {value ? label + ': ' + value : label}
                </li>
            );
        });
    };

    // Same lookup /subscriptions performs, so both pages show the same figure.
    const priceFor = (type) => {
        const list = pricing && pricing[type];
        if (!Array.isArray(list)) return null;
        return (
            list.find((p) => p.currency === currency && p.interval === (isAnnual ? 'year' : 'month')) || null
        );
    };

    const money = (value) =>
        intl.formatNumber(value, {
            style: 'currency',
            currency: isEur ? 'EUR' : 'RON',
            maximumFractionDigits: 2,
        });

    const renderPaidPrice = (type) => {
        const price = priceFor(type);
        if (!price) {
            return null;
        }

        const amount = price.unit_amount / 100;
        // tax_behavior 'exclusive' means VAT is added on top; the existing
        // subscriptions page labels that case " + TVA" via this same key.
        const vat = intl.formatMessage({
            id:
                price.tax_behavior === 'exclusive'
                    ? 'subscriptions.subscription.includeVat'
                    : 'subscriptions.subscription.excludeVat',
        });

        return {
            amount: intl.formatNumber(amount, { maximumFractionDigits: 2 }),
            unit: isEur ? '€' : 'lei',
            period: isAnnual ? t('period.year') : t('period.month'),
            note: (isAnnual ? t('billedAnnually') : t('billedMonthly')) + ' · ' + vat.trim(),
            sub: isAnnual ? t('perMonthEquivalent', { amount: money(amount / 12) }) : t('cancelAnytime'),
        };
    };

    // What the card offers, given the account's relationship to the plan.
    const renderCta = (type, key, primary) => {
        const state = stateOf(type);
        const link = (to, label, isPrimary) => (
            <Link
                to={to}
                className={cx(classes.cta, isPrimary && classes.ctaPrimary)}
                onClick={() => window.logAppActivity(GA_EVENT_TREE.mainPage.onboarding.pricingCta, undefined, { plan: type })}
            >
                {label}
            </Link>
        );

        if (!loggedIn || state === SUBSCRIPTION_STATE_ACTION.START) {
            return link(onboardingLink(type), t(`${key}.cta`), primary);
        }
        if (state === SUBSCRIPTION_STATE_ACTION.ACTIVE) {
            return <span className={cx(classes.cta, classes.ctaCurrent)}>{tp('state.current')}</span>;
        }
        if (state === SUBSCRIPTION_STATE_ACTION.CANCEL) {
            return canceled
                ? link(onboardingLink(type), tp('state.reactivate'), true)
                : link(PROFILE_LINK, tp('state.manage'), false);
        }
        if (state === SUBSCRIPTION_STATE_ACTION.UPGRADE) {
            return canUpgrade ? (
                link(onboardingLink(type), tp('state.upgrade'), primary)
            ) : (
                <p className={classes.ctaNote}>
                    <FormattedMessage id="subscriptions.subscription.mustCancel" />
                </p>
            );
        }
        // A plan below the current one: nothing to offer.
        return null;
    };

    const paidCard = (key, type) => {
        const price = renderPaidPrice(type);
        const featured = type === featuredType;

        return (
            <article key={key} className={cx(classes.card, featured && classes.cardFeatured)}>
                {featured ? (
                    <div className={classes.ribbon}>{isCurrent(type) ? tp('state.current') : t(`${key}.ribbon`)}</div>
                ) : null}
                <h3 className={classes.name}>
                    <FormattedMessage id={`subscriptions.subscription.${type}.name`} />
                </h3>
                <p className={classes.blurb}>{t(`${key}.text`)}</p>

                {price ? (
                    <>
                        <div className={classes.priceRow}>
                            {price.amount}
                            <small className={classes.priceUnit}>{price.unit}</small>
                            <small className={classes.priceUnit}>{price.period}</small>
                        </div>
                        <div className={classes.priceNote}>{price.note}</div>
                        <p className={classes.subNote}>{price.sub}</p>
                        {isAnnual ? <div className={classes.badge}>{t('annualSaving')}</div> : null}
                    </>
                ) : (
                    // Pricing arrives from a public endpoint after mount; keep the
                    // card's rhythm instead of collapsing while it is in flight.
                    <>
                        <div className={classes.priceRow} />
                        <div className={classes.priceNote} />
                        <p className={classes.subNote} />
                    </>
                )}

                <ul className={classes.bullets}>{renderFeatures(type)}</ul>

                {renderCta(type, key, featured)}
            </article>
        );
    };

    const freeFeatured = featuredType === SUBSCRIPTION_PLANS.FREE;

    return (
        <LandingSection id="preturi" paddingTop={heading ? 56 : 8} paddingBottom={64}>
            {heading ? (
                <SectionHeading
                    eyebrow={<FormattedMessage id={heading.eyebrow} />}
                    title={<FormattedMessage id={heading.title} />}
                    lead={<FormattedMessage id={heading.lead} />}
                    marginBottom={40}
                />
            ) : null}

            {canStartTrial ? (
                <div className={classes.trial}>
                    <div>
                        <h3 className={classes.trialTitle}>{tp('trial.title', { days: TRIAL_DAYS })}</h3>
                        <p className={classes.trialText}>{tp('trial.text')}</p>
                    </div>
                    <Link
                        to={onboardingLink(SUBSCRIPTION_PLANS.TRIAL)}
                        className={cx(classes.cta, classes.ctaPrimary)}
                        style={{ width: 'auto', marginTop: 0 }}
                    >
                        {tp('trial.cta')}
                    </Link>
                </div>
            ) : null}

            <div className={classes.toggles}>
                <div role="group" aria-label={t('billing.aria')} className={classes.group}>
                    <Toggle
                        on={!isAnnual}
                        onClick={() => {
                            setBilling(MONTHLY);
                            log(GA_EVENT_TREE.mainPage.onboarding.checkMonthly);
                        }}
                        classes={classes}
                    >
                        {t('billing.monthly')}
                    </Toggle>
                    <Toggle
                        on={isAnnual}
                        onClick={() => {
                            setBilling(ANNUAL);
                            log(GA_EVENT_TREE.mainPage.onboarding.checkAnnual);
                        }}
                        classes={classes}
                    >
                        {t('billing.annual')}
                        <span className={classes.saveBadge}>{t('billing.saveBadge')}</span>
                    </Toggle>
                </div>
                <div role="group" aria-label={t('currency.aria')} className={classes.group}>
                    <Toggle
                        on={!isEur}
                        onClick={() => {
                            setCurrency(RON);
                            log(GA_EVENT_TREE.mainPage.onboarding.checkRon);
                        }}
                        classes={classes}
                    >
                        RON
                    </Toggle>
                    <Toggle
                        on={isEur}
                        onClick={() => {
                            setCurrency(EUR);
                            log(GA_EVENT_TREE.mainPage.onboarding.checkEur);
                        }}
                        classes={classes}
                    >
                        EUR
                    </Toggle>
                </div>
            </div>

            <div className={classes.grid}>
                <article className={cx(classes.card, freeFeatured && classes.cardFeatured)}>
                    {freeFeatured ? <div className={classes.ribbon}>{tp('state.current')}</div> : null}
                    <h3 className={classes.name}>{t('free.name')}</h3>
                    <p className={classes.blurb}>{t('free.text')}</p>
                    <div className={classes.priceRow}>
                        0<small className={classes.priceUnit}>{isEur ? '€' : 'lei'}</small>
                    </div>
                    <div className={classes.priceNote}>{t('free.priceNote')}</div>
                    <p className={classes.subNote}>{t('free.subNote')}</p>
                    <ul className={classes.bullets}>{renderFeatures(SUBSCRIPTION_PLANS.FREE)}</ul>
                    {renderCta(SUBSCRIPTION_PLANS.FREE, 'free', false)}
                </article>

                {paidCard('basic', SUBSCRIPTION_PLANS.BASIC)}
                {/* Teams offers only the base plans; the old page hid Premium there too. */}
                {window.isTeams ? null : paidCard('premium', SUBSCRIPTION_PLANS.PREMIUM)}

                <article className={classes.card}>
                    <h3 className={classes.name}>{t('schools.name')}</h3>
                    <p className={classes.blurb}>{t('schools.text')}</p>
                    <div className={`${classes.priceRow} ${classes.priceRowQuote}`}>
                        {t('schools.priceLabel')}
                        <small className={classes.priceUnit}>{t('schools.priceSuffix')}</small>
                    </div>
                    <div className={classes.priceNote}>{t('schools.priceNote')}</div>
                    <p className={classes.subNote}>{t('schools.subNote')}</p>
                    <div className={classes.badge}>{t('schools.badge')}</div>
                    <ul className={classes.bullets}>
                        {[1, 2, 3].map((n) => (
                            <li key={n} className={classes.bullet}>
                                <span aria-hidden="true" className={classes.tick}>
                                    ✓
                                </span>
                                {t(`schools.bullet${n}`)}
                            </li>
                        ))}
                    </ul>
                    <Link to={SPECIAL_QUOTA_LINK} className={classes.cta}>
                        {t('schools.cta')}
                    </Link>
                </article>
            </div>

            <p className={classes.footnote}>{t('footnote')}</p>
        </LandingSection>
    );
};

const mapStateToProps = (state) => ({
    pricing: state.mainAppState.pricing,
    subscriptions: state.mainAppState.subscriptions,
    profile: state.profile,
});

export default connect(mapStateToProps)(HomePricing);
